import { SLACK_PURPOSE_LABELS, type SlackConversation, type SlackPurpose, type SlackStatus } from '@lia/contracts';
import { Message01Icon, Unlink02Icon } from '@hugeicons/core-free-icons';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { StatusBadge } from '../primitives/badge.js';
import { Button } from '../primitives/button.js';
import { Combobox } from '../primitives/combobox.js';
import { Icon } from '../primitives/icon.js';
import { InlineNotice } from '../primitives/notice.js';

// «Agregar a Slack» (Estateflow's Slack app). Presentational: screens wire the API. Neutral styling (semantic tokens,
// no Slack brand colors). A channel is «Sin verificar» until a test post returned ok:true; the pasted webhook stays as a
// collapsed fallback («Usar un webhook en su lugar»).

export interface SlackAddButtonProps {
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  label?: string;
  className?: string;
}

export function SlackAddButton({ onClick, disabled, loading, label = 'Agregar a Slack', className }: SlackAddButtonProps) {
  return (
    <Button type="button" variant="secondary" disabled={disabled} loading={loading} onClick={onClick} leadingIcon={<Icon icon={Message01Icon} aria-hidden />} className={className}>
      {label}
    </Button>
  );
}

export interface SlackTestOutcome {
  ok: boolean;
  detail: string;
  code: string | null;
}

export interface SlackAppPanelProps {
  status: SlackStatus;
  /** Channels for the picker; null = not loaded (e.g. not installed or revoked). */
  channels: readonly SlackConversation[] | null;
  channelsTruncated?: boolean;
  channelsLoading?: boolean;
  /** Error listing channels (Spanish, from the API). */
  channelsError?: string | null;
  /** Purposes shown here (defaults to all of the scope's purposes). */
  purposes?: readonly SlackPurpose[];
  /** Result of the last test per purpose (this session). */
  lastTest?: Partial<Record<SlackPurpose, SlackTestOutcome>>;
  canManage: boolean;
  busy?: boolean;
  onConnect: () => void;
  onSelectChannel: (purpose: SlackPurpose, channelId: string) => void;
  onTest: (purpose: SlackPurpose) => void;
  onDisconnect: () => void;
  /** Webhook form rendered under «Usar un webhook en su lugar» (collapsed unless the app is unavailable). */
  webhookFallback?: React.ReactNode;
  /** Open the webhook fallback by default (e.g. a webhook is already connected). */
  webhookOpen?: boolean;
  className?: string;
}

const NOT_IN_CHANNEL_HINT = 'Invita a la app al canal privado en Slack (escribe /invite y elige la app de LIA) y vuelve a enviar la prueba.';

/** Installation state, channel picker per purpose, test post and disconnect; webhook fallback collapsed. */
export function SlackAppPanel({
  status,
  channels,
  channelsTruncated,
  channelsLoading,
  channelsError,
  purposes,
  lastTest,
  canManage,
  busy,
  onConnect,
  onSelectChannel,
  onTest,
  onDisconnect,
  webhookFallback,
  webhookOpen,
  className,
}: SlackAppPanelProps) {
  const inst = status.installation;
  const shown = (purposes ?? status.purposes).filter((p) => status.purposes.includes(p));
  const revoked = inst?.status === 'revoked';
  return (
    <div className={cn('grid gap-3', className)}>
      {!status.available && !inst ? (
        <InlineNotice tone="info" role="note">
          Slack no está configurado en la plataforma (falta registrar la app de Slack de Estateflow). Mientras tanto puedes usar un Incoming Webhook.
        </InlineNotice>
      ) : null}

      {status.available && !inst ? (
        <div className="grid gap-2">
          <p className="text-body text-text-secondary">Instala la app de Estateflow en tu Slack y elige el canal. El token del bot se guarda cifrado y no se vuelve a mostrar.</p>
          {canManage ? (
            <div>
              <SlackAddButton onClick={onConnect} disabled={busy} />
            </div>
          ) : null}
        </div>
      ) : null}

      {inst ? (
        <div className="grid gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-body text-text-primary">
              Instalada en <span className="font-medium">{inst.teamName}</span>
            </p>
            {revoked ? <StatusBadge tone="danger">Acceso revocado</StatusBadge> : <StatusBadge tone="neutral">Instalada</StatusBadge>}
          </div>
          {revoked ? (
            <InlineNotice tone="danger">
              <span className="grid gap-2">
                <span>Slack revocó el acceso de la app ({inst.revokedReason ?? 'revocado'}). Los avisos usan el webhook si existe; reconecta para volver a usar la app.</span>
                {canManage && status.available ? (
                  <span>
                    <SlackAddButton onClick={onConnect} disabled={busy} label="Reconectar con Slack" />
                  </span>
                ) : null}
              </span>
            </InlineNotice>
          ) : null}
          {channelsError ? <InlineNotice tone="danger">{channelsError}</InlineNotice> : null}
          {shown.map((purpose) => {
            const choice = status.channels.find((c) => c.purpose === purpose) ?? null;
            const test = lastTest?.[purpose];
            const notInChannel = test?.code === 'not_in_channel' || (!test && !!choice?.lastError?.includes('not_in_channel'));
            const options = (channels ?? []).map((c) => ({
              value: c.id,
              label: `#${c.name}`,
              description: c.isPrivate ? (c.isMember ? 'Privado' : 'Privado · el bot debe estar invitado') : c.isMember ? 'Público · el bot ya está' : 'Público',
            }));
            if (choice && !options.some((o) => o.value === choice.channelId)) options.unshift({ value: choice.channelId, label: `#${choice.channelName}`, description: 'Canal elegido' });
            const pickerId = `slack-channel-${purpose}`;
            return (
              <section key={purpose} aria-labelledby={`${pickerId}-title`} className="grid gap-2 rounded-control border border-border-subtle p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <h4 id={`${pickerId}-title`} className="text-body font-medium text-text-primary">
                    {SLACK_PURPOSE_LABELS[purpose]}
                  </h4>
                  {choice ? <StatusBadge tone={choice.verified ? 'success' : 'warning'}>{choice.verified ? 'Verificado' : 'Sin verificar'}</StatusBadge> : <StatusBadge tone="neutral">Sin canal</StatusBadge>}
                  {choice ? <span className="text-meta text-text-secondary">#{choice.channelName}</span> : null}
                </div>
                {canManage && !revoked ? (
                  <div className="grid gap-1">
                    <label htmlFor={pickerId} className="text-meta text-text-secondary">
                      Canal
                    </label>
                    <Combobox
                      id={pickerId}
                      options={options}
                      value={choice?.channelId ?? null}
                      onValueChange={(v) => v && onSelectChannel(purpose, v)}
                      placeholder={channelsLoading ? 'Cargando canales…' : 'Elegir canal'}
                      searchPlaceholder="Buscar canal…"
                      emptyText="Sin canales"
                      disabled={busy || channelsLoading}
                    />
                    <p className="text-meta text-text-secondary">
                      Los canales públicos funcionan sin invitar al bot. Los privados solo aparecen si la app ya fue invitada.
                      {channelsTruncated ? ' La lista está recortada: busca por nombre.' : ''}
                    </p>
                  </div>
                ) : null}
                {test ? <InlineNotice tone={test.ok ? 'success' : 'danger'}>{test.ok ? 'Slack aceptó el mensaje de prueba.' : test.detail}</InlineNotice> : choice?.lastError ? <InlineNotice tone="danger">Última prueba: {choice.lastError}</InlineNotice> : null}
                {notInChannel ? (
                  <InlineNotice tone="warning" role="note">
                    {NOT_IN_CHANNEL_HINT}
                  </InlineNotice>
                ) : null}
                {canManage && choice && !revoked ? (
                  <div>
                    <Button type="button" size="sm" variant="secondary" disabled={busy} onClick={() => onTest(purpose)}>
                      Enviar prueba
                    </Button>
                  </div>
                ) : null}
              </section>
            );
          })}
          {canManage ? (
            <div>
              <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={onDisconnect} leadingIcon={<Icon icon={Unlink02Icon} aria-hidden />}>
                Desinstalar de Slack
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}

      {webhookFallback ? (
        <details open={webhookOpen || (!status.available && !inst)} className="rounded-control border border-border-subtle p-3">
          <summary className="cursor-pointer rounded-control text-body font-medium text-text-primary focus-visible:outline-2 focus-visible:outline-focus">Usar un webhook en su lugar</summary>
          <div className="mt-3 grid gap-3">
            <p className="text-meta text-text-secondary">Respaldo: si la app de Slack está instalada y activa, los avisos van por la app; el webhook se usa cuando no lo está.</p>
            {webhookFallback}
          </div>
        </details>
      ) : null}
    </div>
  );
}
